

import type { TwiggyConfig, AutoCaptureType, ConsentCategory, TwiggyStorePlatform } from '../types';

export interface ResolvedConfig {
  apiKey: string;
  apiEndpoint: string;
  autoCapture: AutoCaptureType[];
  batchSize: number;
  flushInterval: number;
  consent: {
    required: boolean;
    defaultCategories: ConsentCategory[];
  };
  platform: TwiggyStorePlatform;
  vtex: boolean;
  wishlistDataLayer: boolean;
  debug: boolean;
}

const DEFAULT_API_ENDPOINT = 'https://c908v2t99b.execute-api.sa-east-1.amazonaws.com/prod';

const DEFAULT_CONFIG: Omit<ResolvedConfig, 'apiKey'> = {
  apiEndpoint: DEFAULT_API_ENDPOINT,
  autoCapture: ['pageView'],
  batchSize: 10,
  flushInterval: 5000,
  consent: {
    required: false,
    defaultCategories: ['necessary', 'analytics']
  },
  platform: 'standard',
  vtex: false,
  wishlistDataLayer: false,
  debug: false
};

export function resolveConfig(config: TwiggyConfig): ResolvedConfig {
  if (!config.apiKey) {
    throw new Error('Twiggy: apiKey is required');
  }

  
  const apiKeyParts = config.apiKey.split('_');
  if (apiKeyParts.length < 3 || apiKeyParts[0] !== 'tk') {
    throw new Error('Twiggy: Invalid apiKey format. Expected tk_{tenantSlug}_{random}');
  }

  let platform: TwiggyStorePlatform = 'standard';
  if (config.platform === 'tray') {
    platform = 'tray';
  } else if (config.vtex) {
    platform = 'vtex';
  }

  return {
    apiKey: config.apiKey,
    apiEndpoint: config.apiEndpoint || DEFAULT_CONFIG.apiEndpoint,
    autoCapture: config.autoCapture || DEFAULT_CONFIG.autoCapture,
    batchSize: config.batchSize || DEFAULT_CONFIG.batchSize,
    flushInterval: config.flushInterval || DEFAULT_CONFIG.flushInterval,
    consent: {
      required: config.consent?.required ?? DEFAULT_CONFIG.consent.required,
      defaultCategories:
        config.consent?.defaultCategories || DEFAULT_CONFIG.consent.defaultCategories
    },
    platform,
    vtex: config.vtex ?? DEFAULT_CONFIG.vtex,
    wishlistDataLayer: config.wishlistDataLayer ?? DEFAULT_CONFIG.wishlistDataLayer,
    debug: config.debug ?? DEFAULT_CONFIG.debug
  };
}

export function getTenantSlug(apiKey: string): string {
  const parts = apiKey.split('_');
  if (parts.length >= 3) {
    return parts[1];
  }
  throw new Error('Twiggy: Invalid apiKey format');
}

export function buildApiUrl(config: ResolvedConfig, path: string): string {
  const tenantSlug = getTenantSlug(config.apiKey);
  const baseUrl = config.apiEndpoint.replace(/\/$/, '');
  return `${baseUrl}/${tenantSlug}${path}`;
}
